import { useEffect, useMemo, useState } from "react";
import { ProductService } from "../../services/api";
import { Box, TableCell } from "@mui/material";
import { PaginatedListProduct, Product } from "../../services/swagger";
import useRefresh from "../../hooks/useRefresh";
import { useGlobalContext } from "../../stores/Global";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../organisms/DataView";
import CreateProductDialog from "../organisms/Product/CreateProductDialog";
import EditProductDialog from "../organisms/Product/EditProductDialog";
import { DataTableHeader } from "../organisms/DataTable";
import ItemCard from "../organisms/ItemCard";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export default function ProductsPage() {
  const { t } = useTranslation("Product");
  const { _id } = useParams();
  const navigate = useNavigate();

  const [createDialog, setCreateDialog] = useState(false);
  const [openedProduct, setOpenedProduct] = useState<Product>();
  const [query, setQuery] = useState<QueryParameters>();
  const queryClient = useQueryClient();

  const loadProducts = async (): Promise<PaginatedListProduct | null> => {
    if (!query) return null;
    const res = await ProductService.getProductList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data: products, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadProducts,
    queryKey: ["products", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const loadProduct = async () => {
    if (!_id) return;
    const res = await ProductService.getProduct({ id: _id });
    if (res?.data?.data) setOpenedProduct(res.data.data);
  };

  const openProduct = (product: Product) => {
    setOpenedProduct(product);
  };

  useRefresh(() => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
  }, "product");

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  const handleCloseProduct = () => {
    if (_id) navigate("../");
    setOpenedProduct(undefined);
  };

  useEffect(() => {
    if (_id) loadProduct();
  }, [_id]);

  const headers = useMemo(() => {
    const arr: DataTableHeader[] = [
      {
        field: "itemCode",
        name: t("ItemCode"),
        type: "string",
      },
      {
        field: "name",
        name: t("Name"),
        type: "string",
      },
      {
        field: "group",
        name: t("Group"),
        type: "string",
      },
      {
        field: "price",
        name: t("Price"),
        type: "other",
        renderRowCell: (row: Product) => (
          <TableCell>{row.price?.toFixed(2)}</TableCell>
        ),
      },
      {
        field: "currency",
        name: t("Currency"),
        type: "string",
      },
      {
        field: "totalQuantityInStock",
        name: t("QuantityInStock"),
        type: "number",
      },
      {
        field: "totalAmountUsed",
        name: t("AmountUsed"),
        type: "number",
      },
    ];
    const fieldsToRemove = ["totalQuantityInStock", "totalAmountUsed"];
    if (products?.docs.some((product) => product.warehouseInfo.length > 0))
      return arr;
    const newArr: DataTableHeader[] = [];
    for (const header of arr) {
      if (fieldsToRemove.includes(header.field)) continue;
      newArr.push(header);
    }
    return newArr;
  }, [products]);

  return (
    <Box height={1}>
      <CreateProductDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
      />
      <EditProductDialog product={openedProduct} onClose={handleCloseProduct} />
      <DataView
        name={t("Products")}
        loading={isLoading || !products}
        button={{
          text: t("NewProduct"),
          onClick: () => setCreateDialog(true),
        }}
        onItemClick={(item) => openProduct(item)}
        pagination
        count={products?.count || 0}
        items={products?.docs || []}
        headers={headers}
        onChange={handleDataTableChange}
        queryFunction={ProductService.getProductListQuery}
        renderItem={(product) => (
          <ItemCard product={product} onClick={() => openProduct(product)} />
        )}
      />
    </Box>
  );
}
